import { useId, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { periodRange, type Period } from '@pfo/shared';

import { cx } from '../../../shared/lib/cx.js';
import { dayKeyOf } from '../model/timelineGroups.js';
import { formatVnd } from '../../../shared/lib/money.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import lists from '../../../shared/styles/lists.module.css';
import { EmptyState } from '../../../shared/ui/EmptyState.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { ACCOUNTS_KEY } from '../../accounts/hooks/useAccounts.js';
import { useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import { SaveAsTemplate } from '../../recurring/components/SaveAsTemplate.js';
import { ExpenseForm } from '../components/ExpenseForm.js';
import { IncomeForm } from '../components/IncomeForm.js';
import { SplitForm } from '../components/SplitForm.js';
import { TransferForm } from '../components/TransferForm.js';
import type { TransactionLine } from '../data/timelineRepository.js';
import { voidEntry } from '../data/timelineRepository.js';
import { useQuickAddOptions } from '../hooks/useQuickAdd.js';
import { useTimeline } from '../hooks/useTimeline.js';
import type { TimelineEntry } from '../model/timelineGroups.js';
import { groupTransactionsByDay, timeLabelOf } from '../model/timelineGroups.js';
import { ENTRY_TYPE_LABELS, SOURCE_LABELS } from '../model/transactionLabels.js';
import styles from './TimelinePage.module.css';

/** Only the shapes Quick Add creates can be edited with its forms (steps 1.2 and 1.3). */
const EDITABLE_TYPES = new Set(['expense', 'income', 'transfer', 'debt_payment', 'split']);

/**
 * Timeline — hôm nay / hôm qua / theo ngày, with search, filters, edit and soft delete
 * (spec v2 22.4).
 *
 * Reads `v_transaction_lines`: one row per leg of an entry. An ordinary movement has a single
 * leg and is a single row; a split is rendered as one card listing its destinations, so the
 * money that left the account is visible without pretending it went to one place (step 1.3).
 */
export function TimelinePage() {
  const contextState = useCurrentContext();
  const optionsState = useQuickAddOptions();
  const { mutate } = useSWRConfig();

  const searchId = useId();
  const accountFilterId = useId();
  const categoryFilterId = useId();

  // The dashboard links here to answer "what was this category, that month?" (step 1.4), so
  // both filters can arrive in the URL. They are seeded once into state and then belong to the
  // user: changing the select or dismissing the month chip must not fight the address bar.
  const [searchParams] = useSearchParams();

  const [subjectScope, setSubjectScope] = useState<'self' | 'all'>('self');
  const [search, setSearch] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState(() => searchParams.get('danh-muc') ?? '');
  const [periodFilter, setPeriodFilter] = useState<Period | null>(() =>
    readPeriodParams(searchParams),
  );
  const [editing, setEditing] = useState<TimelineEntry | null>(null);
  /** The row whose actions are open. Only one at a time — the list stays a list. */
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  /** The entry being turned into a recurring template, if any (N9). */
  const [savingTemplateFor, setSavingTemplateFor] = useState<TimelineEntry | null>(null);
  const [confirmingVoid, setConfirmingVoid] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const selfSubjectId = contextState.status === 'ready' ? contextState.context.selfSubject.id : '';
  const timeZone =
    contextState.status === 'ready' ? contextState.context.household.timezone : 'UTC';

  // A month means the month the household lived, cut in its own timezone — the same cut the
  // dashboard's figures used, so the rows here add up to the figure that was tapped.
  const range =
    periodFilter === null ? null : periodRange(periodFilter.year, periodFilter.month, timeZone);

  const timeline = useTimeline({
    subjectScope,
    selfSubjectId,
    ...(search.trim() === '' ? {} : { search }),
    ...(accountId === '' ? {} : { accountId }),
    ...(categoryId === '' ? {} : { categoryId }),
    ...(range === null ? {} : { from: range.fromUtc, to: range.toUtc }),
  });

  if (contextState.status === 'loading') return <LoadingState label="Đang tải giao dịch…" />;
  if (contextState.status === 'error') {
    return <ErrorState message={contextState.message} onRetry={contextState.retry} />;
  }

  const { context } = contextState;

  /**
   * Rereads what a write changed. The list is refreshed through the hook that owns it — a
   * bound mutate is guaranteed to hit this hook's cache entry, while a global key filter has
   * to guess how useSWRInfinite spells it internally.
   */
  async function refreshAfterWrite() {
    await Promise.all([
      timeline.status === 'ready' ? timeline.refresh() : Promise.resolve(),
      mutate(ACCOUNTS_KEY),
    ]);
  }

  async function handleVoid(entry: TimelineEntry) {
    setMessage(null);
    try {
      await voidEntry(entry.id, 'Người dùng xoá trên timeline');
      setConfirmingVoid(null);
      setOpenRowId(null);
      setNotice('Đã xoá giao dịch. Bản ghi vẫn được lưu lại trong lịch sử.');
      await refreshAfterWrite();
    } catch (error) {
      setMessage(toAppError(error).message);
    }
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Giao dịch</h1>

      {/* The household switch only exists once there is a family; personal stays personal (A1). */}
      {context.isFamily ? (
        <div className={styles.tabs} role="tablist" aria-label="Xem giao dịch của">
          {(
            [
              ['self', 'Của tôi'],
              ['all', 'Cả gia đình'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={subjectScope === value}
              className={cx(styles.tab, subjectScope === value && styles.tabActive)}
              onClick={() => setSubjectScope(value)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      {periodFilter !== null ? (
        <p className={styles.periodChip}>
          <span>
            Đang xem tháng {periodFilter.month}/{periodFilter.year}
          </span>
          <button type="button" className={lists.rowButton} onClick={() => setPeriodFilter(null)}>
            Bỏ lọc tháng
          </button>
        </p>
      ) : null}

      <div className={forms.field}>
        <label htmlFor={searchId}>Tìm theo ghi chú</label>
        <input
          id={searchId}
          className={forms.input}
          type="search"
          value={search}
          placeholder="Ví dụ: cà phê"
          enterKeyHint="search"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <details className={styles.filters}>
        <summary className={forms.summary}>Bộ lọc</summary>

        <div className={forms.field}>
          <label htmlFor={accountFilterId}>Tài khoản</label>
          <select
            id={accountFilterId}
            className={forms.input}
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            <option value="">Tất cả tài khoản</option>
            {optionsState.status === 'ready'
              ? optionsState.options.accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))
              : null}
          </select>
        </div>

        <div className={forms.field}>
          <label htmlFor={categoryFilterId}>Danh mục</label>
          <select
            id={categoryFilterId}
            className={forms.input}
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">Tất cả danh mục</option>
            {optionsState.status === 'ready'
              ? optionsState.options.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))
              : null}
          </select>
        </div>
      </details>

      {notice ? (
        <p className={forms.success} role="status">
          {notice}
        </p>
      ) : null}
      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      {editing && optionsState.status === 'ready' ? (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>
            Sửa {ENTRY_TYPE_LABELS[editing.head.entryType]?.toLowerCase() ?? 'giao dịch'}
          </h2>
          {editing.head.entryType === 'expense' ? (
            <ExpenseForm
              options={optionsState.options}
              subjects={{
                selfSubjectId: context.selfSubject.id,
                householdSubjectId: context.householdSubject.id,
              }}
              householdId={context.household.id}
              timeZone={timeZone}
              isFamily={context.isFamily}
              entryId={editing.id}
              initial={{
                amount: editing.head.amount,
                categoryId: editing.head.categoryId ?? '',
                accountId: editing.head.fromAccountId ?? '',
                date: dayKeyOf(editing.head.occurredAt, timeZone),
                note: editing.head.note ?? '',
                forFamily: editing.head.economicSubjectId === context.householdSubject.id,
              }}
              onCancel={() => setEditing(null)}
              onSaved={(saved) => {
                setEditing(null);
                setNotice(saved);
                void refreshAfterWrite();
              }}
            />
          ) : editing.head.entryType === 'split' ? (
            <SplitForm
              options={optionsState.options}
              subjects={{
                selfSubjectId: context.selfSubject.id,
                householdSubjectId: context.householdSubject.id,
              }}
              timeZone={timeZone}
              entryId={editing.id}
              initial={{
                sourceAccountId: editing.head.fromAccountId ?? '',
                date: dayKeyOf(editing.head.occurredAt, timeZone),
                note: editing.head.note ?? '',
                // Every leg of the entry, because saving REPLACES the lines: a form seeded
                // with a subset would quietly delete the rest (A7).
                allocations: editing.legs.map((leg) => ({
                  kind: leg.categoryId === null ? ('transfer' as const) : ('expense' as const),
                  targetId: leg.categoryId ?? leg.toAccountId ?? '',
                  amount: leg.amount,
                  purpose: leg.purpose,
                })),
              }}
              onCancel={() => setEditing(null)}
              onSaved={(saved) => {
                setEditing(null);
                setNotice(saved);
                void refreshAfterWrite();
              }}
            />
          ) : editing.head.entryType === 'transfer' || editing.head.entryType === 'debt_payment' ? (
            <TransferForm
              options={optionsState.options}
              subjects={{
                selfSubjectId: context.selfSubject.id,
                householdSubjectId: context.householdSubject.id,
              }}
              timeZone={timeZone}
              entryId={editing.id}
              initial={{
                amount: editing.head.amount,
                fromAccountId: editing.head.fromAccountId ?? '',
                toAccountId: editing.head.toAccountId ?? '',
                date: dayKeyOf(editing.head.occurredAt, timeZone),
                note: editing.head.note ?? '',
                purpose: editing.head.purpose ?? '',
              }}
              onCancel={() => setEditing(null)}
              onSaved={(saved) => {
                setEditing(null);
                setNotice(saved);
                void refreshAfterWrite();
              }}
            />
          ) : (
            <IncomeForm
              options={optionsState.options}
              subjects={{
                selfSubjectId: context.selfSubject.id,
                householdSubjectId: context.householdSubject.id,
              }}
              timeZone={timeZone}
              entryId={editing.id}
              initial={{
                amount: editing.head.amount,
                incomeSourceId: editing.head.incomeSourceId ?? '',
                destinationAccountId: editing.head.toAccountId ?? '',
                date: dayKeyOf(editing.head.occurredAt, timeZone),
                note: editing.head.note ?? '',
              }}
              onCancel={() => setEditing(null)}
              onSaved={(saved) => {
                setEditing(null);
                setNotice(saved);
                void refreshAfterWrite();
              }}
            />
          )}
        </section>
      ) : null}

      {savingTemplateFor ? (
        <section className={lists.card}>
          <h2 className={lists.cardTitle}>Lưu thành khoản định kỳ</h2>
          <SaveAsTemplate
            entryId={savingTemplateFor.id}
            suggestedName={titleOf(savingTemplateFor)}
            defaultFirstDue={nextMonthOf(savingTemplateFor.head.occurredAt, timeZone)}
            onCancel={() => setSavingTemplateFor(null)}
            onSaved={(name) => {
              setSavingTemplateFor(null);
              setNotice(`Đã lưu "${name}" thành khoản định kỳ. Đến hạn sẽ hiện ở Trang chủ.`);
            }}
          />
        </section>
      ) : null}

      {timeline.status === 'loading' ? <LoadingState label="Đang tải giao dịch…" /> : null}
      {timeline.status === 'error' ? (
        <ErrorState message={timeline.message} onRetry={timeline.retry} />
      ) : null}

      {timeline.status === 'ready' && timeline.items.length === 0 ? (
        <EmptyState
          icon="🧾"
          title="Chưa có giao dịch nào"
          message={
            search.trim() === '' && accountId === '' && categoryId === ''
              ? 'Bấm ⊕ để ghi khoản chi đầu tiên — chỉ mất vài giây.'
              : 'Không có giao dịch nào khớp bộ lọc hiện tại.'
          }
        />
      ) : null}

      {timeline.status === 'ready' && timeline.items.length > 0
        ? groupTransactionsByDay(timeline.items, { timeZone, now: new Date() }).map((group) => (
            <section key={group.dayKey} className={lists.card}>
              <h2 className={lists.cardTitle}>{group.label}</h2>
              <ul className={lists.list}>
                {group.items.map((entry) => (
                  <li key={entry.id}>
                    {/* The whole row is the target — a 44px line beats hunting for a pencil,
                        and an action bar on EVERY row made each entry three lines tall with
                        a red box on it (PO, 2026-08-19). Same fix the category rows got. */}
                    <button
                      type="button"
                      className={styles.row}
                      aria-expanded={openRowId === entry.id}
                      onClick={() => {
                        setOpenRowId((current) => (current === entry.id ? null : entry.id));
                        setConfirmingVoid(null);
                        setNotice(null);
                      }}
                    >
                      <span className={lists.rowText}>
                        <span className={lists.rowName}>{titleOf(entry)}</span>
                        <span className={lists.rowMeta}>
                          {timeLabelOf(entry.head.occurredAt, timeZone)} · {subtitleOf(entry)}
                          {subjectScope === 'all' ? ` · ${entry.head.economicSubjectSlug}` : ''}
                          {SOURCE_LABELS[entry.head.source]
                            ? ` · ${SOURCE_LABELS[entry.head.source]}`
                            : ''}
                        </span>
                        {entry.head.note ? (
                          <span className={styles.note}>{entry.head.note}</span>
                        ) : null}
                      </span>

                      {/* A split with only some legs on screen (a category filter matched one
                          of them) shows no total: the rows below would contradict it. */}
                      {entry.isSplit && !entry.isComplete ? null : (
                        <span
                          className={cx(
                            lists.amount,
                            entry.head.entryType === 'income' && styles.amountIncome,
                          )}
                        >
                          {signOf(entry.head)}
                          {formatVnd(entry.isSplit ? entry.head.entryAmount : entry.head.amount)}
                        </span>
                      )}
                      <span className={lists.chevron} aria-hidden="true">
                        ›
                      </span>
                    </button>

                    {entry.isSplit ? (
                      <ul className={styles.legs}>
                        {entry.legs.map((leg) => (
                          <li key={leg.lineId} className={styles.leg}>
                            <span>{legTitleOf(leg)}</span>
                            <span className={styles.legAmount}>{formatVnd(leg.amount)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    {openRowId === entry.id ? (
                      <div className={styles.rowActions}>
                        {isEditable(entry) ? (
                          <button
                            type="button"
                            className={styles.actionButton}
                            onClick={() => {
                              setEditing(entry);
                              setOpenRowId(null);
                              setNotice(null);
                            }}
                          >
                            Sửa
                          </button>
                        ) : null}

                        {/* Every line of the entry is copied, so a five-way salary split
                            becomes a five-line template without re-typing it (N9). */}
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => {
                            setSavingTemplateFor(entry);
                            setOpenRowId(null);
                            setNotice(null);
                          }}
                        >
                          Lưu thành định kỳ
                        </button>

                        {confirmingVoid === entry.id ? (
                          <>
                            <button
                              type="button"
                              className={styles.actionButton}
                              onClick={() => setConfirmingVoid(null)}
                            >
                              Hủy
                            </button>
                            <button
                              type="button"
                              className={cx(styles.actionButton, styles.actionButtonDanger)}
                              onClick={() => void handleVoid(entry)}
                            >
                              Xoá thật
                            </button>
                          </>
                        ) : (
                          // Deleting money data is never one tap (skill 13.2); and it is a soft
                          // delete — the row leaves the reports, the record stays (spec 3.3).
                          <button
                            type="button"
                            className={cx(styles.actionButton, styles.actionButtonDanger)}
                            onClick={() => setConfirmingVoid(entry.id)}
                          >
                            Xoá
                          </button>
                        )}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))
        : null}

      {timeline.status === 'ready' && timeline.hasMore ? (
        <button
          type="button"
          className={forms.secondary}
          disabled={timeline.isLoadingMore}
          onClick={timeline.loadMore}
        >
          {timeline.isLoadingMore ? 'Đang tải…' : 'Xem thêm'}
        </button>
      ) : null}
    </div>
  );
}

/** What the row is about: the category for an expense, the source for income. */
/** `?nam=2026&thang=8` — ignored unless both are a real calendar month. */
function readPeriodParams(params: URLSearchParams): Period | null {
  const year = Number(params.get('nam'));
  const month = Number(params.get('thang'));
  if (!Number.isInteger(year) || year < 1970) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  return { year, month };
}

function titleOf(entry: TimelineEntry): string {
  const { head } = entry;
  if (entry.isSplit) return head.description ?? 'Chia tiền';
  if (head.categoryName) return head.categoryName;
  if (head.incomeSourceName) return head.incomeSourceName;
  if (head.description) return head.description;
  return ENTRY_TYPE_LABELS[head.entryType] ?? 'Giao dịch';
}

/** Where the money moved, in the family's words — never a ledger account name. */
function subtitleOf(entry: TimelineEntry): string {
  const { fromAccountName: from, toAccountName: to } = entry.head;
  if (entry.isSplit) {
    // line_count counts the credit line as well, so the destinations are one fewer.
    const legs = `${entry.head.lineCount - 1} khoản`;
    return from ? `từ ${from} · ${legs}` : legs;
  }
  if (from && to) return `${from} → ${to}`;
  if (to) return `vào ${to}`;
  if (from) return `từ ${from}`;
  return ENTRY_TYPE_LABELS[entry.head.entryType] ?? '';
}

/** One destination of a split: the category it paid, or the account it went to. */
function legTitleOf(leg: TransactionLine): string {
  return leg.categoryName ?? leg.toAccountName ?? 'Khoản chia';
}

/** Money in is the only case worth a sign: everything else on this screen is money leaving. */
function signOf(line: TransactionLine): string {
  return line.entryType === 'income' ? '+' : '';
}

/**
 * A suggestion for when the same thing happens again: one month on from the entry, on the
 * same day. Calendar arithmetic on a YYYY-MM-DD key — deliberately not money.
 */
function nextMonthOf(instant: string, timeZone: string): string {
  const dayKey = dayKeyOf(instant, timeZone);
  const date = new Date(`${dayKey}T00:00:00Z`);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + 1);
  // Clamp to the length of the target month, the same way app.next_due_date does.
  const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, lastDay));
  return date.toISOString().slice(0, 10);
}

/**
 * A split can only be edited when every leg is on screen: saving REPLACES the entry's lines,
 * so a form seeded from a filtered subset would delete the legs it never saw.
 */
function isEditable(entry: TimelineEntry): boolean {
  if (!EDITABLE_TYPES.has(entry.head.entryType)) return false;
  return !entry.isSplit || entry.isComplete;
}
